import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Users, ListChecks, Tags, Moon, Sun, RotateCcw, Briefcase, CalendarDays, Lock, FolderOpen, BadgeCheck, LogOut, ClipboardList, UserCog, Trophy, Camera, SlidersHorizontal, FolderKanban, NotebookPen, BookOpen } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, tid: "navigation-dashboard-tab", end: true },
  { to: "/pipeline", label: "Pipeline CRM", icon: KanbanSquare, tid: "navigation-crm-tab" },
  { to: "/liste", label: "Liste Contatti", icon: ClipboardList, tid: "navigation-liste-tab" },
  { to: "/clienti", label: "Clienti", icon: BadgeCheck, tid: "navigation-clienti-tab" },
  { to: "/portfolio", label: "Portfolio", icon: Briefcase, tid: "navigation-portfolio-tab" },
  { to: "/programmazione", label: "Programmazione", icon: CalendarDays, tid: "navigation-programmazione-tab" },
  { to: "/materiali", label: "Materiali", icon: FolderOpen, tid: "navigation-materiali-tab" },
  { to: "/collaboratori", label: "Collaboratori", icon: Users, tid: "navigation-collaborators-tab" },
  { to: "/modello", label: "Modello Operativo", icon: ListChecks, tid: "navigation-modello-tab" },
  { to: "/listino", label: "Listino Prezzi", icon: Tags, tid: "navigation-listino-tab" },
  { to: "/progetti", label: "Experience", icon: FolderKanban, tid: "navigation-progetti-tab", onlyRitr: true },
  { to: "/agenda", label: "Agenda", icon: NotebookPen, tid: "navigation-agenda-tab", titolareOnly: true },
  { to: "/guida", label: "Guida", icon: BookOpen, tid: "navigation-guida-tab" },
  { to: "/impostazioni", label: "Impostazioni", icon: SlidersHorizontal, tid: "navigation-impostazioni-tab", titolareOnly: true },
  { to: "/controllo", label: "Controllo", icon: Lock, tid: "navigation-controllo-tab", titolareOnly: true },
];

export function Nav() {
  const { theme, toggle } = useTheme();
  const { api, refresh } = useCrm();
  const { user, isTitolare, logout, activeSettore, setActiveSettore } = useAuth();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);

  const visible = items.filter((it) => (!it.titolareOnly || isTitolare) && !(activeSettore === "ritrattistica" && it.to === "/portfolio") && !(it.onlyRitr && activeSettore !== "ritrattistica"));

  const doReset = async () => {
    setResetting(true);
    try {
      await api.seed();
      await refresh();
      toast.success("Dati demo ripristinati");
      navigate("/");
    } catch (e) {
      toast.error("Errore nel ripristino");
    } finally {
      setResetting(false);
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/70 dark:border-slate-800/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <img src="/logo-efferre.png" alt="EFFERRE Photo" className="h-9 w-auto object-contain" data-testid="nav-logo" />
            <div className="leading-tight hidden sm:block">
              <div className="font-extrabold tracking-tight text-slate-900 dark:text-white">EFFERRE Photo</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">CRM Collaboratori</div>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="hidden sm:flex items-center gap-1.5 text-xs mr-1">
              <span className="font-medium text-slate-700 dark:text-slate-200" data-testid="nav-user-name">{user?.nome || user?.email}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${isTitolare ? "bg-primary/15 text-primary" : "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300"}`}>
                {isTitolare ? "Titolare" : "Collaboratore"}
              </span>
            </span>

            {isTitolare && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="reset-demo-button" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                    <RotateCcw className="h-3.5 w-3.5" /> Reset Demo
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Ripristinare i dati demo?</AlertDialogTitle>
                    <AlertDialogDescription>Verranno cancellati tutti i dati attuali (SSD, collaboratori, impostazioni) e ricaricato il set dimostrativo.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="reset-cancel-button">Annulla</AlertDialogCancel>
                    <AlertDialogAction data-testid="reset-confirm-button" onClick={doReset} disabled={resetting}>{resetting ? "Ripristino..." : "Ripristina"}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            <button data-testid="account-button" onClick={() => navigate("/account")} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Account">
              <UserCog className="h-5 w-5" />
            </button>
            <button data-testid="theme-toggle-button" onClick={toggle} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Cambia tema">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button data-testid="logout-button" onClick={logout} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-destructive hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" aria-label="Esci">
              <LogOut className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>

        {isTitolare && (
          <div className="flex items-center gap-1.5 pb-2" data-testid="sector-switch">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Settore</span>
            <button data-testid="sector-sportivo" onClick={() => setActiveSettore("sportivo")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${activeSettore === "sportivo" ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
              <Trophy className="h-3.5 w-3.5" /> Sportivo
            </button>
            <button data-testid="sector-ritrattistica" onClick={() => setActiveSettore("ritrattistica")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${activeSettore === "ritrattistica" ? "bg-primary text-white border-primary" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
              <Camera className="h-3.5 w-3.5" /> Ritrattistica
            </button>
          </div>
        )}

        <nav className="flex items-center gap-x-1 overflow-x-auto kanban-scroll -mb-px xl:flex-wrap xl:overflow-visible">
          {visible.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.tid}
              className={({ isActive }) => `flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${isActive ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
              <it.icon className="h-4 w-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
